/**
 * @description Component-TradeLimitForm
 */
import './index.less';

import { PEARL_SPACING, Side, TickMath } from '@synfutures/sdks-perp';
import { useDebounceEffect, useDebounceFn } from 'ahooks';
import { Form } from 'antd';
// import BN from 'bignumber.js';
import classNames from 'classnames';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Alert from '@/components/Alert';
import FormInput from '@/components/FormInput';
import SynInputNumber from '@/components/SynInputNumber';
import { FETCHING_STATUS } from '@/constants';
import { TRADE_TYPE } from '@/constants/trade';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { useWrappedPortfolioByPairId } from '@/features/account/portfolioHook';
import {
  useCurrentPairFromUrl,
  useGetAlignedPrice,
  useGetDefaultLimitPrice,
  usePairLimitPriceRange,
} from '@/features/pair/hook';
import { orderSimulate, setTradeFormType } from '@/features/trade/actions';
import {
  useCallSimulateCrossMarketOrder,
  useCanPlaceCrossMarketOrder,
  useLimitFormState,
  useLimitFormStatus,
  useLimitPriceChangeWhenOutOfRange,
  useLimitSimulation,
  useSimulateCrossMarketMinMarginAndAmount,
  useTradeSide,
  useTradeStoreTransformed,
} from '@/features/trade/hooks';
import { useSDK } from '@/features/web3/hook';
import { useAppDispatch } from '@/hooks';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';
// import TradeMarginRequire from '@/pages/components/TradeMarginRequire';
import TradeFormFooter from '@/pages/trade/TradeCard/TradeForm/tradeformFooter';
import { formatNumber, inputNumChecker, toBN, toWad } from '@/utils/numberUtil';

import { useWrappedOrderList } from '@/features/account/orderHook';
import { useAvailableTokenBalance } from '@/features/balance/hook';
import { useTradeStore } from '@/features/trade/store';
import TradeFormAlert from '../TradeFormAlert';
import TradeFormBase from '../TradeFormBase';
import CrossMarketCheckbox from './CrossMarketCheckbox';
import { CrossMarketDetail } from './CrossMarketDetail';
import { LimitFormDetail } from './LimitFormDetail';
interface IPropTypes {
  children?: React.ReactNode;
  className?: string;
}
const TradeLimitForm: FC<IPropTypes> = function ({ className }) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const chainId = useChainId();
  const userAddr = useUserAddr();
  const { leverage } = useTradeStoreTransformed();
  const tradeSide = useTradeSide(chainId);
  const limitFormState = useLimitFormState(chainId);
  const limitFormStatus = useLimitFormStatus(chainId);
  const currentPair = useCurrentPairFromUrl(chainId);
  const [inputTouched, setInputTouched] = useState(false);
  const [priceInputFocused, setPriceInputFocused] = useState(false);
  const tokenBalance = useAvailableTokenBalance(currentPair?.rootInstrument.marginToken?.address, chainId);
  const sdkContext = useSDK(chainId);
  const orders = useWrappedOrderList(chainId, userAddr, currentPair?.id);
  const portfolio = useWrappedPortfolioByPairId(chainId, userAddr, currentPair?.id);
  const limitSimulation = useLimitSimulation(chainId);
  const {
    isTurnOnCrossMarket,
    fromOrderBook,
    toLimitTab,
    setLimitPrice,
    setTick,
    setAlignedLimitPrice,
    setPairLeverage,
  } = useTradeStore();

  const callSimulateCrossMarketOrder = useCallSimulateCrossMarketOrder({
    chainId,
    userAddr,
    tradeSide,
    currentPair,
    baseAmount: WrappedBigNumber.from(limitFormState.baseAmount || 0),
    limitPrice: limitFormState.limitPrice,
  });
  const changelimitPriceWhenOutOfRange = useLimitPriceChangeWhenOutOfRange(
    chainId,
    priceInputFocused,
    isTurnOnCrossMarket,
  );
  // const defaultLeverage = useTradeDefaultLeverage(currentPair?.maxLeverage);
  const [lowerPrice, upperPrice] = usePairLimitPriceRange(currentPair, tradeSide, isTurnOnCrossMarket);

  const getDefaultLimitPrice = useGetDefaultLimitPrice(currentPair, tradeSide);
  const getAlignPriceWad = useGetAlignedPrice(currentPair);
  const canPlaceCrossMarketOrder = useCanPlaceCrossMarketOrder(chainId);
  const crossMarginMinMarginAndAmountSimulation = useSimulateCrossMarketMinMarginAndAmount(chainId);

  // const isCrossMarket = use();
  // const limitSimulation = useLimitSimulationRaw(chainId);
  const limitPriceStrChanged = useCallback(
    async (inputAmountStr: string, tick?: number) => {
      gtag('event', 'adjust_price', {
        adjust_price: 'enter', //plus_or_mins, enter, click_order_book
      });
      inputAmountStr = inputNumChecker(inputAmountStr, currentPair?.priceDecimal);
      if (!tick) {
        const alignedPrice = getAlignPriceWad(inputAmountStr, tradeSide);
        setLimitPrice(inputAmountStr);
        if (alignedPrice) {
          setTick(alignedPrice.tick);
          setAlignedLimitPrice(inputNumChecker(alignedPrice.adjustPrice.stringValue || '', currentPair?.priceDecimal));
        }
      } else {
        setLimitPrice(inputAmountStr);
        setTick(tick);
        setAlignedLimitPrice(inputNumChecker(inputAmountStr || '', currentPair?.priceDecimal));
      }
    },
    [currentPair?.priceDecimal, getAlignPriceWad, setAlignedLimitPrice, setLimitPrice, setTick, tradeSide],
  );
  const setDefaultLimitPrice = useCallback(
    async (tradeSide?: Side) => {
      const defaultLimitPrice = getDefaultLimitPrice(tradeSide);
      defaultLimitPrice &&
        limitPriceStrChanged(
          formatNumber(defaultLimitPrice?.adjustPrice.stringValue, currentPair?.priceDecimal, false),
          defaultLimitPrice?.tick,
        );
    },
    [currentPair?.priceDecimal, getDefaultLimitPrice, limitPriceStrChanged],
  );
  const bestBidAsk = useCallback(() => {
    const adjustSide = tradeSide;
    let price;
    if (adjustSide === Side.LONG && upperPrice) {
      price = upperPrice;
    }
    if (adjustSide === Side.SHORT && lowerPrice) {
      price = lowerPrice;
    }
    if (price) {
      setLimitPrice(price.stringValue);
      setAlignedLimitPrice(price.stringValue);
    }
  }, [lowerPrice, setAlignedLimitPrice, setLimitPrice, tradeSide, upperPrice]);
  const incrementTick = useCallback(
    async (multiple: number) => {
      try {
        if (currentPair) {
          const resultTick = limitFormState?.tick || currentPair.tick;
          // if (limitFormState?.tick !== undefined) {
          //   resultTick = limitFormState.tick;
          // }

          const newTick = resultTick + PEARL_SPACING * multiple;
          const newPrice = WrappedBigNumber.from(TickMath.getWadAtTick(newTick));

          const limitPrice = formatNumber(
            newPrice.stringValue,
            currentPair?.priceDecimal,
            false,
            // multiple > 0 ? BN.ROUND_DOWN : BN.ROUND_CEIL,
          );
          setLimitPrice(limitPrice);
          setAlignedLimitPrice(limitPrice);
          setTick(newTick);
        }
      } catch (error) {
        console.log('🚀 ~ file: index.tsx:132 ~ error:', error);
      }
    },
    [currentPair, limitFormState?.tick, setAlignedLimitPrice, setLimitPrice, setTick],
  );

  const fetchOrderSimulation = useCallback(
    (leverage?: string) => {
      if (chainId && currentPair && sdkContext && userAddr) {
        if (canPlaceCrossMarketOrder && leverage && WrappedBigNumber.from(limitFormState.baseAmount).gt(0)) {
          callSimulateCrossMarketOrder(leverage);
        } else {
          dispatch(
            orderSimulate({
              chainId,
              sdkContext,
              base: toWad(limitFormState.baseAmount?.trim() || 0),
              tradeSide,
              limitPrice: limitFormState.limitPrice,
              pair: currentPair,
              userAddr,
              leverage: leverage ? toWad(leverage) : undefined,
            }),
          );
        }
      }
    },
    [
      chainId,
      sdkContext,
      canPlaceCrossMarketOrder,
      limitFormState.baseAmount,
      limitFormState.limitPrice,
      callSimulateCrossMarketOrder,
      dispatch,
      tradeSide,
      currentPair,
      userAddr,
    ],
  );

  const { run: debouncedSimulateOrder } = useDebounceFn(
    (leverage: string | undefined) => {
      fetchOrderSimulation(leverage);
    },
    { wait: 500 },
  );

  const leverageChanged = useCallback(
    (leverage: string) => {
      if (chainId) {
        const baseWad = toWad(limitFormState.baseAmount || 0);
        if (userAddr && currentPair) {
          setPairLeverage(userAddr, chainId, currentPair.id, leverage);
        }
        // no simulation when base zero
        !baseWad.eq(0) && debouncedSimulateOrder(leverage);
      }
    },
    [chainId, currentPair, debouncedSimulateOrder, limitFormState.baseAmount, setPairLeverage, userAddr],
  );

  useEffect(() => {
    debouncedSimulateOrder(leverage);
    // when limit price change or base amount change or order map change or token balance change
  }, [
    limitFormState.limitPrice,
    orders?.length,
    limitFormState.baseAmount,
    tokenBalance?.stringValue,
    debouncedSimulateOrder,
    leverage,
  ]);

  // useEffect(() => {
  //   if (chainId && userAddr && currentPair?.id) {
  //     const leverageSaved = getSavedPairLeverage(userAddr, chainId, currentPair.id, TRADE_TYPE.LIMIT);

  //     dispatch(
  //       setTradeFormLeverage({
  //         chainId,
  //         leverage: leverageSaved || defaultLeverage.toString(),
  //       }),
  //     );
  //   }
  //   //  only depend on chainId userAddr and currentPair.id
  // }, [chainId, userAddr, currentPair?.id]);

  const onNativeTokenDepositClick = useCallback(() => {
    chainId &&
      dispatch(
        setTradeFormType({
          chainId,
          tradeType: TRADE_TYPE.DEPOSIT_NATIVE,
        }),
      );
  }, [chainId, dispatch]);

  const onMaxBalanceClick = useCallback(
    (balance: WrappedBigNumber) => {
      console.log('🚀 ~ balance:', balance);
      if (chainId && portfolio && sdkContext) {
        // TODO: adapt order simulate with margin
        // dispatch(
        //   orderSimulate({
        //     chainId,
        //     sdkContext,
        //     base: toWad(limitFormState.baseAmount || 0),
        //     tradeSide,
        //     limitPrice: limitFormState.limitPrice,
        //     portfolio,
        //     leverage: undefined,
        //     // margin: balance.stringValue,
        //   }),
        // );
      }
    },
    [chainId, portfolio, sdkContext],
  );

  const adjustLimitPrice = useMemo(() => {
    return limitFormState?.limitPrice ? inputNumChecker(limitFormState?.limitPrice, currentPair?.priceDecimal) : '';
  }, [currentPair?.priceDecimal, limitFormState?.limitPrice]);

  const footer = useMemo(() => <TradeFormFooter tradeType={TRADE_TYPE.LIMIT} />, []);

  const alignLimitPrice = useMemo(() => {
    return limitFormState?.limitPrice;
  }, [limitFormState?.limitPrice]);
  useEffect(() => {
    const crossMarket = document.querySelector('.syn-cross-market-checkbox-text');
    if (lowerPrice && lowerPrice?.formatLiqPriceString().length >= 9 && crossMarket) {
      crossMarket.classList.add('short');
    } else {
      crossMarket?.classList.remove('short');
    }
    // must depend on this
  }, [lowerPrice, lowerPrice?.stringValue]);
  useDebounceEffect(() => {
    setInputTouched(false);
    if (!toLimitTab && !fromOrderBook) {
      setDefaultLimitPrice(tradeSide);
    }
  }, [tradeSide, toLimitTab, fromOrderBook]);
  return (
    <Form className={classNames('syn-trade-limit-form', className)}>
      {/* <TradeFormSide
        disabled={limitFormStatus === FETCHING_STATUS.FETCHING}
        chainId={chainId}
        onSideChange={(key) => {
          const tradeSide = Number(key) as Side;
          setInputTouched(false);
          setDefaultLimitPrice(tradeSide);
          chainId &&
            dispatch(
              setTradeFormSide({
                chainId,
                tradeSide,
              }),
            );
        }}
      /> */}
      <div className="syn-trade-limit-form-section">
        <div className="syn-trade-limit-form-section-title-wrap" id="priceRange">
          <div className="syn-trade-limit-form-section-title">
            <span className="syn-trade-limit-form-section-title-range">
              {t('common.price')} ({lowerPrice?.formatLiqPriceNumberWithTooltip()} -{' '}
              {upperPrice?.formatLiqPriceNumberWithTooltip()})
            </span>
            {!isTurnOnCrossMarket && (
              <div className="syn-trade-limit-form-section-title-bid" onClick={bestBidAsk}>
                {tradeSide === Side.LONG ? t('common.tradePage.bestBid') : t('common.tradePage.bestAsk')}
              </div>
            )}
          </div>
          <CrossMarketCheckbox chainId={chainId} />
        </div>
        <div className="syn-trade-limit-form-price-row">
          <FormInput
            disabled={limitFormStatus === FETCHING_STATUS.FETCHING}
            inputProps={{
              type: 'text',
              pattern: '^[0-9]*[.,]?[0-9]*$',
              inputMode: 'decimal',
              autoComplete: 'off',
              autoCorrect: 'off',
              step: 1e18,
              onKeyDown: (e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault(),
            }}
            onFocus={() => {
              setInputTouched(false);
              setPriceInputFocused(true);
            }}
            onBlur={() => {
              setInputTouched(true);
              setPriceInputFocused(false);
              changelimitPriceWhenOutOfRange();
            }}
            tokenInfo={currentPair?.rootInstrument.priceToken}
            inputAmountStr={adjustLimitPrice}
            inputAmountStrChanged={limitPriceStrChanged}
            suffix={currentPair?.rootInstrument.priceToken.symbol}
          />
          <SynInputNumber
            disabled={limitFormStatus === FETCHING_STATUS.FETCHING}
            inputChanged={incrementTick}
            upperPrice={upperPrice}
            lowerPrice={lowerPrice}
            limitPrice={adjustLimitPrice}
            // pair={currentPair}
            className={classNames('syn-trade-limit-form-number-tick')}
            minDisableTooltip={t('tooltip.tradePage.minLimitDisabled')}
            maxDisableTooltip={t('tooltip.tradePage.maxLimitDisabled')}
          />
        </div>
        {inputTouched &&
          lowerPrice?.lte(limitFormState.limitPrice || 0) &&
          upperPrice?.gte(limitFormState.limitPrice || 0) &&
          limitFormState.limitPrice &&
          WrappedBigNumber.from(limitFormState.alignedPrice || 0)?.notEq(limitFormState.limitPrice) && (
            <Alert
              type="info"
              showIcon
              message={
                <Trans
                  i18nKey="common.tradePage.limitSub"
                  components={{
                    b: <span />,
                  }}
                  values={{
                    price: limitFormState.alignedPrice,
                  }}
                />
              }
            />
          )}
        {canPlaceCrossMarketOrder && (
          <Alert
            type="info"
            showIcon
            message={
              <Trans
                i18nKey={
                  crossMarginMinMarginAndAmountSimulation?.data
                    ? 'tooltip.tradePage.hasBeenCrossedWithMargin'
                    : 'tooltip.tradePage.hasBeenCrossed'
                }
                components={{
                  b: <b />,
                }}
                values={{
                  base: currentPair?.rootInstrument.baseToken.symbol,
                  size: crossMarginMinMarginAndAmountSimulation?.data?.tradeSize?.formatDisplayNumber(),
                }}
              />
            }
          />
        )}
      </div>
      <TradeFormBase
        state={limitFormState}
        price={alignLimitPrice}
        disabled={limitFormStatus === FETCHING_STATUS.FETCHING}
        baseToken={currentPair?.rootInstrument.baseToken}
        quoteToken={currentPair?.rootInstrument.quoteToken}
        tradeSide={tradeSide}
        pair={currentPair}
        leverageChanged={leverageChanged}
        marginToken={currentPair?.rootInstrument.marginToken}
      />
      {/* <TradeMarginRequire
        margin={WrappedBigNumber.from(limitSimulation?.data?.margin || 0)}
        quote={currentPair?.rootInstrument?.marginToken}></TradeMarginRequire> */}
      {footer}
      <TradeFormAlert
        chainId={chainId}
        tradeType={TRADE_TYPE.LIMIT}
        tradeSimulation={undefined}
        marginToken={currentPair?.rootInstrument?.marginToken}
        onMaxBalanceClick={onMaxBalanceClick}
        onNativeTokenDepositClick={onNativeTokenDepositClick}
        currentPair={currentPair}
      />
      {/* {toBN(limitFormState.baseAmount || 0).gt(0) && !limitSimulation?.message && ( */}
      {toBN(limitFormState.baseAmount || 0).gt(0) &&
        (canPlaceCrossMarketOrder ? (
          <CrossMarketDetail
            marginToken={currentPair?.rootInstrument.marginToken}
            fairPrice={currentPair?.wrapAttribute('fairPrice')}
            isLoading={false}
            chainId={chainId}
            pairSymbol={currentPair?.symbol}
            tradeSide={tradeSide}
            isInverse={currentPair?.isInverse}
            currentPair={currentPair}
          />
        ) : (
          <LimitFormDetail
            marginToken={currentPair?.rootInstrument.marginToken}
            fairPrice={currentPair?.wrapAttribute('fairPrice')}
            isLoading={false}
            chainId={chainId}
            pairSymbol={currentPair?.symbol}
            tradeSide={tradeSide}
            simulationData={limitSimulation?.data}
          />
        ))}
    </Form>
  );
};

export default TradeLimitForm;
